import { formatMessage, reconciliationResultMessages } from "@/services/messages";
import { useTabActions } from "@/stores/tab-store";
import { AlertTriangle } from "lucide-react";

/**
 * LIMIT_EXCEEDED баннер (T061, FR-023).
 *
 * Fullscreen с `AlertTriangle`, заголовком «Слишком много расхождений ({totalCount})»,
 * subtitle «Разбейте набор на несколько меньших...», CTA:
 * - «Открыть журнал активности»
 * - «Закрыть» (если onClose передан)
 *
 * Виртуальный скролл и фильтры НЕ рендерятся (FR-023). Сводка-totals
 * (`severityBreakdown`) отображается в баннере inline.
 */
export interface LimitExceededBannerProps {
	totalCount: number;
	severityBreakdown: { error: number; warning: number };
	setName: string;
	onClose?: () => void;
}

export function LimitExceededBanner({
	totalCount,
	severityBreakdown,
	setName,
	onClose,
}: LimitExceededBannerProps) {
	const messages = reconciliationResultMessages.limit;
	const banner = reconciliationResultMessages.banner;
	const { openActivityLogTab } = useTabActions();

	return (
		<section className="mx-auto max-w-3xl rounded-lg border border-yellow-200 bg-white px-6 py-12 text-center">
			<AlertTriangle className="mx-auto h-12 w-12 text-yellow-500" aria-hidden="true" />
			<h2 className="mt-4 text-lg font-semibold text-yellow-900">
				{formatMessage(messages.title_template, { totalCount })}
			</h2>
			<p className="mt-2 text-sm text-yellow-800">{messages.subtitle}</p>

			{/* Сводка-totals. */}
			<dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
				<MetaItem label={banner.set_name_label}>{setName}</MetaItem>
				<MetaItem label={banner.totals_label}>
					{formatMessage(banner.totals_template, {
						total: totalCount,
						error: severityBreakdown.error,
						warning: severityBreakdown.warning,
					})}
				</MetaItem>
			</dl>

			<div className="mt-6 flex flex-wrap items-center justify-center gap-2">
				<button
					type="button"
					onClick={openActivityLogTab}
					className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
				>
					{messages.open_logs_cta}
				</button>
				{onClose && (
					<button
						type="button"
						onClick={onClose}
						className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
					>
						{messages.close_cta}
					</button>
				)}
			</div>
		</section>
	);
}

function MetaItem({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div>
			<dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
			<dd className="mt-0.5 text-sm font-medium text-slate-900">{children}</dd>
		</div>
	);
}
